// The 🟢 / 🔴 N / 🔵 badge every Flutter device card shows in its top-left
// corner, backed by the cached liveness lookup in core/devices/deviceStatus.
//
// ⚠️ 🟢 = "heard from in 20 minutes", NOT "will take a command" — an ESP8684
// posts a network ping over HTTP every 3 minutes even with its MQTT link down
// (16091206, 2026-10-17). TAP the badge to ask the device itself
// (core/devices/liveness probeOne): ✅ answered / ❌ no answer, for a minute.
// The tap never reaches the card underneath (which would open the device).

import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, useTheme } from '@mui/material';
import { getStatusCached, hasNoHeartbeat } from '../../../core/devices/deviceStatus';
import { probeOne } from '../../../core/devices/liveness';

const PROBE_SHOWN_MS = 60000;

export default function LivenessBadge({ uniqueNumber }) {
  const [value, setValue] = useState(null);
  const [probe, setProbe] = useState(null);   // null | 'busy' | { ok, ms }
  const theme = useTheme();
  // A BLE lamp group has no heartbeat: no badge at all (see hasNoHeartbeat).
  const none = hasNoHeartbeat(uniqueNumber);

  useEffect(() => {
    if (!uniqueNumber || none) return undefined;
    let alive = true;
    setValue(null);
    setProbe(null);
    getStatusCached(uniqueNumber).then((v) => { if (alive) setValue(v); });
    return () => { alive = false; };
  }, [uniqueNumber, none]);

  useEffect(() => {
    if (!probe || probe === 'busy') return undefined;
    const id = setTimeout(() => setProbe(null), PROBE_SHOWN_MS);
    return () => clearTimeout(id);
  }, [probe]);

  if (none) return null;

  const check = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (probe === 'busy') return;
    setProbe('busy');
    setProbe(await probeOne(uniqueNumber).catch(() => ({ ok: null })));
  };

  let shown = value;
  if (probe === 'busy') shown = null;
  else if (probe && probe.ok === true) shown = '✅';
  else if (probe && probe.ok === false) shown = '❌ no answer';
  else if (probe && probe.ok === null) shown = '🔵';

  return (
    <Box
      component="span"
      role="button"
      onClick={check}
      // A long-press on the card is its own gesture; keep it off the badge too.
      onTouchStart={(e) => e.stopPropagation()}
      sx={{
        fontSize: 14,
        fontWeight: 700,
        color: theme.sli.text,
        textShadow: '0 0 2px rgba(0,0,0,0.54)',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      {shown == null ? <CircularProgress size={16} thickness={2} /> : shown}
    </Box>
  );
}
